import exifr from 'exifr';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { embedExifIntoJpegBlob } from './exif-embed';
import { FileIntakeResult, FileMetadata } from './kyc-file-metadata.types';

// Ensure the pdfjs worker is configured even if this module loads before shared/pdf.
if (!GlobalWorkerOptions.workerSrc) {
  GlobalWorkerOptions.workerSrc = 'assets/pdf.worker.min.js';
}

const HEIC_JPEG_QUALITY = 0.92;

// C0 control range, minus \t \n \r (kept — harmless and legitimate in JSON/Postgres text).
const CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g;

/**
 * Recursively strips C0 control characters from every string read out of EXIF/PDF metadata.
 * Some binary tags (e.g. IPTC ApplicationRecordVersion, a 2-byte binary value) get misread as
 * text by exifr, embedding a real NUL byte; Postgres rejects that NUL when parsing the
 * file_metadata jsonb at insert time (CPK-4715). A field that is entirely control characters
 * is dropped rather than stored as "". Dates pass through untouched.
 */
function sanitizeMetadataStrings<T>(value: T): T {
  if (typeof value === 'string') {
    return value.replace(CONTROL_CHARS, '') as unknown as T;
  }
  if (value === null || typeof value !== 'object' || value instanceof Date) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((v) => sanitizeMetadataStrings(v)) as unknown as T;
  }
  const out: Record<string, unknown> = {};
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    const cleaned = sanitizeMetadataStrings(v);
    if (typeof v === 'string' && v.length > 0 && cleaned === '') continue;
    out[key] = cleaned;
  }
  return out as unknown as T;
}

function baseName(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

function isPdf(head: Uint8Array): boolean {
  // %PDF
  return head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46;
}

/** HEIC/HEIF sniff: ISO-BMFF 'ftyp' with a heic/heif/mif1 brand, or name/mime hint. */
function isHeic(file: File, head: Uint8Array): boolean {
  const nameHit = /\.hei[cf]$/i.test(file.name);
  const mimeHit = /image\/hei[cf]/i.test(file.type);
  const brand =
    head.length >= 12 &&
    head[4] === 0x66 && head[5] === 0x74 && head[6] === 0x79 && head[7] === 0x70; // 'ftyp'
  const brandStr = brand
    ? String.fromCharCode(head[8], head[9], head[10], head[11]).toLowerCase()
    : '';
  const brandHit = ['heic', 'heix', 'heim', 'heis', 'hevc', 'mif1', 'msf1'].includes(brandStr);
  return nameHit || mimeHit || brandHit;
}

function baseMeta(file: File): FileMetadata {
  return {
    file: {
      originalName: file.name,
      mimeType: file.type || '',
      sizeBytes: file.size,
      lastModified: file.lastModified,
    },
    source: 'upload',
  };
}

function detectImageType(head: Uint8Array): 'jpeg' | 'png' | 'other' {
  if (head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return 'jpeg';
  if (head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47) return 'png';
  return 'other';
}

async function blobToJpegViaCanvas(blob: Blob): Promise<Blob> {
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = () => rej(new Error('undecodable image'));
      im.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    return await new Promise<Blob>((res, rej) =>
      canvas.toBlob((b) => (b ? res(b) : rej(new Error('toBlob failed'))), 'image/jpeg', 0.92),
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function intakeImage(file: File, meta: FileMetadata, head: Uint8Array): Promise<FileIntakeResult> {
  let exif: Record<string, unknown> | null = null;
  try {
    const raw = (await exifr.parse(file, true)) as Record<string, unknown> | undefined;
    exif = raw ? sanitizeMetadataStrings(raw) : null;
  } catch {
    exif = null;
  }

  const kind = detectImageType(head);
  if (kind === 'jpeg' || kind === 'png') {
    const ext = kind === 'jpeg' ? '.jpg' : '.png';
    return {
      displayBlob: file,
      storageBlob: file,
      storageName: baseName(file.name) + ext,
      metadata: { ...meta, exif },
    };
  }

  // Not a backend-safe format (webp/bmp/tiff/avif/etc.): transcode to JPEG.
  // Let failures (undecodable image) propagate to intakeUploadedFile's outer catch.
  let jpeg = await blobToJpegViaCanvas(file);

  try {
    if (exif) {
      jpeg = await embedExifIntoJpegBlob(jpeg, {
        make: (exif['Make'] as string) || undefined,
        model: (exif['Model'] as string) || undefined,
        software: (exif['Software'] as string) || undefined,
        dateTimeOriginal: (exif['DateTimeOriginal'] as any)
          ? String(exif['DateTimeOriginal']).replace(/-/g, ':').replace('T', ' ').slice(0, 19)
          : undefined,
      });
    }
  } catch {
    /* keep unembedded jpeg — DB column still has faithful exif */
  }

  const storageName = baseName(file.name) + '.jpg';
  return {
    displayBlob: jpeg,
    storageBlob: jpeg,
    storageName,
    metadata: { ...meta, exif, conversion: { from: file.type || 'image', to: 'image/jpeg' } },
  };
}

async function intakePdf(file: File, meta: FileMetadata): Promise<FileIntakeResult> {
  let pdf: Record<string, unknown> | null = null;
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const doc = await getDocument({ data: bytes.slice() }).promise;
    const info = await doc.getMetadata();
    pdf = sanitizeMetadataStrings({ ...(info.info as any), pageCount: doc.numPages });
  } catch {
    pdf = null;
  }
  return {
    displayBlob: file,
    storageBlob: file,
    storageName: file.name,
    metadata: { ...meta, pdf },
  };
}

async function intakeHeic(file: File, meta: FileMetadata): Promise<FileIntakeResult> {
  // Read EXIF from the ORIGINAL HEIC first (heic2any output drops it).
  let exif: Record<string, unknown> | null = null;
  try {
    const raw = (await exifr.parse(file, true)) as Record<string, unknown> | undefined;
    exif = raw ? sanitizeMetadataStrings(raw) : null;
  } catch {
    exif = null;
  }
  // Lazy-load heic2any (heavy wasm) only when a HEIC is actually seen.
  const heic2any = (await import('heic2any')).default as (opts: any) => Promise<Blob | Blob[]>;
  const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: HEIC_JPEG_QUALITY });
  let jpeg = Array.isArray(converted) ? converted[0] : converted;

  // Re-inject the original EXIF into the converted JPEG (drop Orientation — pixels
  // are already upright after decode, and a stale tag would double-rotate viewers).
  try {
    if (exif) {
      jpeg = await embedExifIntoJpegBlob(jpeg, {
        make: (exif['Make'] as string) || undefined,
        model: (exif['Model'] as string) || undefined,
        software: (exif['Software'] as string) || undefined,
        dateTimeOriginal: (exif['DateTimeOriginal'] as any)
          ? String(exif['DateTimeOriginal']).replace(/-/g, ':').replace('T', ' ').slice(0, 19)
          : undefined,
      });
    }
  } catch {
    /* keep unembedded jpeg — DB column still has faithful exif */
  }

  const storageName = baseName(file.name) + '.jpg';
  return {
    displayBlob: jpeg,
    storageBlob: jpeg,
    storageName,
    metadata: { ...meta, exif, conversion: { from: file.type || 'image/heic', to: 'image/jpeg' } },
  };
}

/**
 * Read metadata from the original bytes and normalize the file to a
 * browser-displayable, storable form. Fail-open: on any error, returns the
 * original file with file-level metadata only.
 */
export async function intakeUploadedFile(file: File): Promise<FileIntakeResult> {
  const meta = baseMeta(file);
  try {
    const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
    if (isPdf(head)) return await intakePdf(file, meta);
    if (isHeic(file, head)) return await intakeHeic(file, meta);
    return await intakeImage(file, meta, head);
  } catch {
    return { displayBlob: file, storageBlob: file, storageName: file.name, metadata: meta };
  }
}
